import React, { useEffect, useMemo, useState } from 'react'
import { ODUL_ONERI_TOPICS } from '../../helper/odulOneriDepartments'
import { previewRelatedDepartmentManager } from '../../helper/odulOneriDepartmentResolve'
import { ODUL_FACILITIES, resolveDepartmentFacility } from '../../helper/odulOneriFacilities'
import useOdulOneriCall from '../../hooks/useOdulOneriCall'
import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_MAX_FILES,
  formatFileSize,
  validateAttachment,
} from '../../helper/odulOneriImplementation'
import './OdulOneriBasvuruFormu.css'

const INITIAL_FORM = {
  fullName: '',
  employeeDept: '',
  role: '',
  phone: '',
  email: '',
  location: '',
  relatedDepartmentName: '',
  topic: '',
  currentState: '',
  proposedState: '',
}

/** Ödül Öneri başvuru formu — HTML 1 prototip görünümü */
const OdulOneriBasvuruFormu = ({ handleClose, embedded = false }) => {
  const { fetchDepartments, createOdulOneri, uploadCurrentStateAttachments } =
    useOdulOneriCall()

  const [formData, setFormData] = useState(INITIAL_FORM)
  const [departments, setDepartments] = useState([])
  const [attachmentFiles, setAttachmentFiles] = useState([])
  const [attachmentError, setAttachmentError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    let mounted = true
    ;(async () => {
      const list = await fetchDepartments()
      if (mounted && list?.length) setDepartments(list)
    })()
    return () => {
      mounted = false
    }
  }, [fetchDepartments])

  const facilityDepartments = useMemo(() => {
    if (!formData.location) return []
    const facility = resolveDepartmentFacility(formData.location)
    return departments.filter(
      (dept) => resolveDepartmentFacility(dept.facility) === facility
    )
  }, [departments, formData.location])

  const relatedPreview = useMemo(() => {
    if (!formData.location || !formData.relatedDepartmentName) return null
    const selected = facilityDepartments.find(
      (d) => d.name === formData.relatedDepartmentName
    )
    return previewRelatedDepartmentManager(
      {
        relatedDepartmentId: selected?.id || '',
        relatedDepartmentName: formData.relatedDepartmentName,
        facility: formData.location,
      },
      departments
    )
  }, [
    formData.relatedDepartmentName,
    formData.location,
    facilityDepartments,
    departments,
  ])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => {
      if (name === 'location') {
        const facility = resolveDepartmentFacility(value)
        const scoped = departments.filter(
          (dept) => resolveDepartmentFacility(dept.facility) === facility
        )
        const keepEmployee = scoped.some((d) => d.name === prev.employeeDept)
        const keepRelated = scoped.some((d) => d.name === prev.relatedDepartmentName)
        return {
          ...prev,
          location: value,
          employeeDept: keepEmployee ? prev.employeeDept : '',
          relatedDepartmentName: keepRelated ? prev.relatedDepartmentName : '',
        }
      }
      return { ...prev, [name]: value }
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!relatedPreview?.ok) return

    setSubmitting(true)
    try {
      const selectedDept = facilityDepartments.find(
        (dept) => dept.name === formData.relatedDepartmentName
      )
      let currentStateAttachments = []
      if (attachmentFiles.length) {
        currentStateAttachments = await uploadCurrentStateAttachments(attachmentFiles)
      }
      await createOdulOneri({
        ...formData,
        relatedDepartmentId: selectedDept?.id || '',
        currentStateAttachments,
      })
      setFormData(INITIAL_FORM)
      setAttachmentFiles([])
      setAttachmentError('')
      if (typeof handleClose === 'function') handleClose()
    } catch (_) {
      /* toast hook içinde */
    } finally {
      setSubmitting(false)
    }
  }

  const canSubmit =
    !submitting &&
    formData.fullName &&
    formData.employeeDept &&
    formData.role &&
    formData.phone &&
    formData.location &&
    formData.relatedDepartmentName &&
    formData.topic &&
    formData.currentState &&
    formData.proposedState &&
    relatedPreview?.ok

  return (
    <div className={`odul-basvuru${embedded ? ' embedded' : ''}`}>
      <div className="header-title">Öneri Başvuru Formu</div>
      <div className="warning-text">
        Geri bildirimlerinizin takibi ve durumunu öğrenmek için telefon numarası doğruluğu
        önemlidir !
      </div>

      <form onSubmit={handleSubmit} noValidate>
        <div className="form-group">
          <input
            type="text"
            name="fullName"
            placeholder="Adınız Soyadınız *"
            required
            value={formData.fullName}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <input
            type="text"
            name="role"
            placeholder="Göreviniz *"
            required
            value={formData.role}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <input
            type="tel"
            name="phone"
            placeholder="Telefon (0xxx-xxx-xxxx) *"
            required
            value={formData.phone}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <input
            type="email"
            name="email"
            placeholder="Eposta"
            value={formData.email}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <select
            name="location"
            required
            value={formData.location}
            onChange={handleChange}
          >
            <option value="" disabled>
              Çalıştığınız Lokasyonu Seçiniz *
            </option>
            {ODUL_FACILITIES.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <select
            name="employeeDept"
            required
            disabled={!formData.location}
            value={formData.employeeDept}
            onChange={handleChange}
          >
            <option value="" disabled>
              Çalıştığınız Bölüm / Birimi Seçiniz *
            </option>
            {facilityDepartments.map((dept) => (
              <option key={`emp-${dept.id}`} value={dept.name}>
                {dept.name}
              </option>
            ))}
          </select>
          <div className="field-note">
            Not: Görev yaptığınız bölüm veya birimi listeden seçiniz.
          </div>
        </div>

        <div className="form-group">
          <select
            name="relatedDepartmentName"
            required
            disabled={!formData.location}
            value={formData.relatedDepartmentName}
            onChange={handleChange}
          >
            <option value="" disabled>
              Önerinin İlgili Olduğu Bölüm / Birimi Seçiniz *
            </option>
            {facilityDepartments.map((dept) => (
              <option key={dept.id} value={dept.name}>
                {dept.name}
              </option>
            ))}
          </select>
          <div className="field-note">
            Not: Önerinin ilgili olduğu bölüm veya birimi listeden seçiniz.
          </div>
          {formData.relatedDepartmentName && relatedPreview && !relatedPreview.ok && (
            <div className="field-error">{relatedPreview.error}</div>
          )}
          {formData.location && facilityDepartments.length === 0 && (
            <div className="field-error">
              Bu tesis için henüz bölüm/yönetici tanımlı değil.
            </div>
          )}
        </div>

        <div className="form-group">
          <select name="topic" required value={formData.topic} onChange={handleChange}>
            <option value="" disabled>
              Öneri Konusu Seçiniz *
            </option>
            {ODUL_ONERI_TOPICS.map((topic) => (
              <option key={topic} value={topic}>
                {topic}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <div className="info-note">
            Mevcut durumu ve önerdiğiniz çözümü ayrı ayrı tanımlayınız.
          </div>
        </div>

        <div className="form-group" style={{ width: '100%' }}>
          <div className="current-state-layout">
            <div className="current-state-field">
              <div className="section-label">Mevcut Durum (Sorun / İsraf Nedir?) *</div>
              <textarea
                name="currentState"
                required
                placeholder="Yaşanan problemi, israfı veya aksaklığı detaylıca tanımlayınız..."
                value={formData.currentState}
                onChange={handleChange}
              />
            </div>
            <div className="current-state-attachments">
              <div className="section-label">
                Mevcut Duruma Ait Fotoğraf / Doküman (İsteğe Bağlı)
              </div>
              <div className="attachment-area">
                <p className="attachment-help">
                  Mevcut problemi, israfı veya aksaklığı gösteren fotoğraf ve destekleyici
                  dosyaları ekleyin. Birden fazla dosya seçebilirsiniz. Görsel, PDF, Word,
                  Excel ve PowerPoint dosyaları desteklenir. (En fazla {ATTACHMENT_MAX_FILES}{' '}
                  dosya)
                </p>
                <label className="attachment-button" htmlFor="odul-attachment-input">
                  Dosya Seç
                </label>
                <input
                  id="odul-attachment-input"
                  type="file"
                  multiple
                  accept={ATTACHMENT_ACCEPT}
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const list = Array.from(e.target.files || []).slice(
                      0,
                      ATTACHMENT_MAX_FILES
                    )
                    for (const file of list) {
                      const err = validateAttachment(file)
                      if (err) {
                        setAttachmentError(err)
                        return
                      }
                    }
                    setAttachmentError('')
                    setAttachmentFiles(list)
                    e.target.value = ''
                  }}
                />
                <ul className="attachment-list" aria-live="polite">
                  {attachmentFiles.map((f) => (
                    <li key={`${f.name}-${f.size}`}>
                      <span className="attachment-name">
                        {f.name}{' '}
                        <span className="attachment-size">({formatFileSize(f.size)})</span>
                      </span>
                      <button
                        type="button"
                        className="attachment-remove"
                        onClick={() =>
                          setAttachmentFiles((prev) => prev.filter((x) => x !== f))
                        }
                      >
                        Kaldır
                      </button>
                    </li>
                  ))}
                </ul>
                {attachmentError && <div className="field-error">{attachmentError}</div>}
              </div>
            </div>
          </div>
        </div>

        <div className="form-group">
          <div className="onerilen-wrap">
            <div className="section-label">
              Önerilen Durum (Çözümünüz / Öneriniz Nedir?) *
            </div>
            <textarea
              name="proposedState"
              required
              placeholder="Geliştirme fikrinizi ve bunun kuruma sağlayacağı katkıları yazınız..."
              value={formData.proposedState}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="form-group">
          <button className="btn-submit" type="submit" disabled={!canSubmit}>
            {submitting ? 'Kaydediliyor…' : 'Kaydet'}
          </button>
        </div>
      </form>
    </div>
  )
}

export default OdulOneriBasvuruFormu
